import React, { useState } from "react";
import gdpr_badge from "../../../assets/images/gdpr-badge.png";
import kz_logo from "../../../assets/images/kz_logo.png";
import Button from "../../components/Elements/Button";
import Input from "../../components/Elements/Input";
import { getTranslation } from "../../hooks/utils";
import { csrfToken } from "../../utis/csrfTokenUtils";
import TermsAndPolicyContent from "../../utis/TermsAndPolicyContent";
import TermsAndUseContent from "../../utis/TermsAndUseContent";
import DropDown from "../Elements/DropDown";
import TermsAndPolicyModal from "../Modals/TermsAndPolicyModal";
import "./authenticationStyles.css";
import ThanksSection from "./ThanksSection";

const gettanslationStringOnBehalfOfStatus = {
    login: "devise.log_in_header",
    confirmation: "devise.headers.resend_confirmation_instructions",
    unlock: "devise.headers.resend_unlock_instructions",
    forgotPassword: "devise.headers.recover_password",
    signup: "devise.logo_texts.try_kz_free",
};

const loginErrorMessages = [
    "user_exist",
    "invalid_password",
    "not_found_in_database",
];

const formActionURL = {
    login: "/users/login",
    confirmation: "/users/confirmation",
    unlock: "/users/unlock",
    forgotPassword: "/users/password",
    signup: "/signup",
};

const GetMessage = ({ message }) => {
    let content;
    switch (message) {
        case "send_instructions":
        case "locked":
            content = (
                <>
                    {getTranslation("devise.failure.locked1_html")} Please{" "}
                    <a href="https://support.boxonline.com">contact us</a> to reactivate it
                </>
            );
            break;
        case "invalid_password":
        case "not_found_in_database":
            content = <>{getTranslation("devise.failure.invalid1")}</>;
            break;
        case "last_attempt":
            content = getTranslation("devise.failure.last_attempt1");
            break;
        default:
            content = "";
    }
    return <>{content}</>;
};

const Login = ({ props, status }) => {
    const [termsModalStatus, setTermsModalStatus] = useState(false);
    const [policyModalStatus, setPolicyModalStatus] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [sessionExpired] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const fromParam = params.get("session_expired") === "true";
        const fromStorage = localStorage.getItem("sessionExpired") === "true";
        const expired = fromParam || fromStorage;
        if (expired) {
            localStorage.removeItem("sessionExpired");
            params.delete("session_expired");
            const newUrl = params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, "", newUrl);
        }
        return expired;
    });

    // Signup password handling
    const [signupPassword, setSignupPassword] = useState("");
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [emailError, setEmailError] = useState("");

    const { error, messages } = props;
    const passwordRules = {
        minLength: signupPassword.length >= 6,
        uppercase: /[A-Z]/.test(signupPassword),
        digit: /\d/.test(signupPassword),
        special: /[^A-Za-z0-9]/.test(signupPassword),
    };

    const allRulesValid = Object.values(passwordRules).every(Boolean);

    const handleSignupSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);

        const payload = {
            user: {
                email: formData.get("user[email]"),
                password: formData.get("user[password]"),
                terms_accepted: true,
            },
        };

        try {
            const response = await fetch("/signup", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRF-Token": csrfToken(),
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                setSignupSuccess(true);
            } else {
                setPasswordErrors(data?.field_errors?.password || []);
                setEmailError(data?.field_errors?.email?.[0] || "");
            }
        } catch (err) {
            console.error("Signup failed", err);
        }
    };

    const getAlertMessage = (type, error) => {
        if (error && error[type]) {
            return <span className="has-error">{error[type].join("")}</span>;
        }
        return "";
    };

    return (
        <div className="login-form-container">
            <form
                acceptCharset="UTF-8"
                className="form-main-auth text-center"
                action={formActionURL[status]}
                method="post"
                onSubmit={status === "signup" ? handleSignupSubmit : undefined}
            >
                {status === "signup" && (
                    <input name="utf8" type="hidden" value="✓" autoComplete="off" />
                )}

                <input type="hidden" value={csrfToken()} name="authenticity_token" />

                <img src={kz_logo} alt="Logo" className="login-form-logo" />

                <div className="login-form-auth-wrapper">
                    {signupSuccess || (messages?.text && messages.text.includes("Thanks")) ? (
                        <ThanksSection />
                    ) : (
                        <>
                            {gettanslationStringOnBehalfOfStatus[status] && (
                                <div
                                    className={`login-form-heading ${
                                        status === "signup" ? "signup-header" : ""
                                    }`}
                                >
                                    {getTranslation(gettanslationStringOnBehalfOfStatus[status])}
                                </div>
                            )}

                            {status === "signup" && (
                                <div className="signup-alert">
                                    {`${getTranslation(
                                        "devise.logo_texts.no_card"
                                    )} ${getTranslation("devise.logo_texts.cancel_anytime")}`}
                                </div>
                            )}

                            {sessionExpired && (
                                <div className="session-expired-banner">
                                    <div className="session-expired-text">
                                        <strong>Your session has expired</strong>
                                        Please log in again to continue.
                                    </div>
                                </div>
                            )}

                            {props?.messages?.alert && (
                                <div className="d-flex justify-start alert-devise">
                                    <GetMessage message={props.messages.alert} />
                                </div>
                            )}

                            {/* Email */}
                            <Input
                                type="email"
                                placeholder="Email"
                                name="user[email]"
                                required
                                errorMessageStatus={error?.email !== undefined}
                            >
                                {getAlertMessage("email", error)}
                                {emailError && (
                                    <span className="has-error">{emailError}</span>
                                )}
                            </Input>

                            {/* LOGIN */}
                            {status === "login" && (
                                <>
                                    <Input
                                        type="password"
                                        placeholder="Password"
                                        name="user[password]"
                                        required
                                    />

                                    <Input
                                        classList="remember-me-wrapper p-0 text-start mt-4"
                                        className="me-1 login-check form-check-input"
                                        type="checkbox"
                                        name="user[remember_me]"
                                        id="rememberMeCheckbox"
                                    >
                    <span className="d-flex justify-content-between align-items-center w-100">
                      <label className="mb-0 small" htmlFor="rememberMeCheckbox">Remember Me</label>
                      <img
                          src={gdpr_badge}
                          alt="GDPR Badge"
                          height="25"
                          onClick={() => setPolicyModalStatus(true)}
                          style={{ cursor: "pointer" }}
                      />
                    </span>
                                    </Input>

                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text="Log In"
                                    />

                                    <div className="signup-action-wrapper">
                                        <p>
                                            Don't have an account?{" "}
                                            <a href="/signup" className="text-underline">
                                                Sign Up
                                            </a>
                                        </p>
                                        <p className="text-underline">
                                            <a href="/users/password/new">Forgot password?</a>
                                        </p>
                                    </div>

                                    <div className="terms-wrapper">
                                        <p><a onClick={() => setTermsModalStatus(true)}>
                                            Terms of Service
                                            </a>
                                        </p>
                                        <DropDown
                                            title={<span>options</span>}
                                            classList="d-flex align-self-end"
                                            customClassList="text-dropdown"
                                            optionList={[
                                                {
                                                    url: "/users/confirmation/new",
                                                    title: "Resend Activation Link",
                                                },
                                                { url: "/users/unlock/new", title: "Unlock Account" },
                                                {
                                                    url: "/users/password/new",
                                                    title: "Forgot password?",
                                                },
                                            ]}
                                        />
                                    </div>
                                </>
                            )}

                            {/* CONFIRM / UNLOCK / FORGOT */}
                            {(status === "confirmation" ||
                                status === "unlock" ||
                                status === "forgotPassword") && (
                                <>
                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text={getTranslation(
                                            gettanslationStringOnBehalfOfStatus[status]
                                        )}
                                    />
                                    <div className="d-flex justify-content-between">
                                        <a href="/users/login">Login</a>
                                        <a href="/signup">Sign Up</a>
                                    </div>
                                </>
                            )}

                            {/* SIGNUP */}
                            {status === "signup" && (
                                <>
                                    <Input
                                        type="password"
                                        placeholder="Password"
                                        name="user[password]"
                                        required
                                        onChange={(e) => {
                                            setSignupPassword(e.target.value);
                                            setPasswordErrors([]);
                                            setPasswordTouched(true);
                                        }}
                                    />

                                    {passwordTouched && (
                                        <div className="password-rules text-start mb-3">
                                            <p className={passwordRules.minLength ? "valid" : "invalid"}>
                                                • Minimum 6 characters
                                            </p>
                                            <p className={passwordRules.uppercase ? "valid" : "invalid"}>
                                                • At least one uppercase letter
                                            </p>
                                            <p className={passwordRules.digit ? "valid" : "invalid"}>
                                                • At least one digit
                                            </p>
                                            <p className={passwordRules.special ? "valid" : "invalid"}>
                                                • At least one special character
                                            </p>
                                        </div>
                                    )}

                                    <Input type="hidden" name="user[terms_accepted]" value />

                                    <Button
                                        classList="login-btn"
                                        type="submit"
                                        text="Create My Account »"
                                        disabled={!allRulesValid}
                                    />

                                    <p className="terms-text">
                                        By continuing, you agree to the{" "}
                                        <a
                                            href="javascript:void(0)"
                                            onClick={() => setTermsModalStatus(true)}
                                        >
                                            Terms of Service
                                        </a>
                                    </p>

                                    <p className="terms-text large">
                                        Already have an account?{" "}
                                        <a href="/users/login" className="text-decoration-underline">
                                            Log In
                                        </a>
                                    </p>
                                </>
                            )}
                        </>
                    )}
                </div>
            </form>

            <TermsAndPolicyModal
                modalStatus={termsModalStatus}
                modalActiveStatus={setTermsModalStatus}
                title="Terms of Use and Service"
            >
                <TermsAndUseContent />
            </TermsAndPolicyModal>

            <TermsAndPolicyModal
                modalStatus={policyModalStatus}
                modalActiveStatus={setPolicyModalStatus}
                title="Privacy Notice"
            >
                <TermsAndPolicyContent />
            </TermsAndPolicyModal>
        </div>
    );
};

export default Login;
